<template>
  <section class="events-page">
    <!-- Hero Section -->
    <div class="events-hero">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            {{ $t?.('events.heroTitle') || 'Próximas Actuacións' }}
          </h1>
          <p class="hero-subtitle">
            {{ $t?.('events.heroSubtitle') || 'Non te perdas as nosas próximas actuacións e concertos' }}
          </p>
          <div class="hero-decoration">
            <div class="music-note">♪</div>
            <div class="music-note">♫</div>
            <div class="music-note">♬</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Calendar Section -->
    <div class="calendar-section section">
      <div class="container">
        <Card variant="elevated" padding="lg" class="calendar-card">
          <div class="calendar-header">
            <h2 class="calendar-title">
              <i class="fas fa-calendar-alt"></i>
              {{ $t?.('events.calendarTitle') || 'Calendario de Eventos' }}
            </h2>
            <p class="calendar-description">
              {{ $t?.('events.calendarDescription') || 'Consulta todas as nosas actuacións e eventos programados' }}
            </p>
          </div>

          <div class="calendar-wrapper">
            <iframe
              src="https://calendar.google.com/calendar/embed?src=b62365ca1fbe277747178b17b54ba479ef83310d77d75d79fddf2f1002a0b6ec%40group.calendar.google.com&ctz=Europe%2FMadrid&mode=AGENDA"
              class="calendar-iframe"
              frameborder="0"
              title="Calendario de eventos"
              loading="lazy"
            ></iframe>
          </div>
        </Card>
      </div>
    </div>

    <!-- Info Cards Section -->
    <div class="info-section section">
      <div class="container">
        <div class="info-grid">
          <Card variant="elevated" padding="lg" class="info-card">
            <div class="info-icon">
              <i class="fas fa-music"></i>
            </div>
            <h3 class="info-title">{{ $t?.('events.concertosTitle') || 'Concertos' }}</h3>
            <p class="info-text">
              {{ $t?.('events.concertosText') || 'Actuamos en diferentes eventos ao longo do ano, desde festas patronais ata concertos especiais.' }}
            </p>
          </Card>

          <Card variant="elevated" padding="lg" class="info-card">
            <div class="info-icon">
              <i class="fas fa-users"></i>
            </div>
            <h3 class="info-title">{{ $t?.('events.procesionsTitle') || 'Procesións' }}</h3>
            <p class="info-text">
              {{ $t?.('events.procesionsText') || 'Acompañamos as principais procesións e eventos relixiosos da comarca con a nosa música.' }}
            </p>
          </Card>

          <Card variant="elevated" padding="lg" class="info-card">
            <div class="info-icon">
              <i class="fas fa-star"></i>
            </div>
            <h3 class="info-title">{{ $t?.('events.specialTitle') || 'Eventos Especiais' }}</h3>
            <p class="info-text">
              {{ $t?.('events.specialText') || 'Participamos en certames, intercambios culturais e eventos especiais dentro e fóra de Galicia.' }}
            </p>
          </Card>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="cta-section section">
      <div class="container">
        <Card variant="elevated" padding="lg" class="cta-card">
          <div class="cta-content">
            <h2 class="cta-title">{{ $t?.('events.ctaTitle') || 'Queres que actuemos no teu evento?' }}</h2>
            <p class="cta-text">
              {{ $t?.('events.ctaText') || 'Estamos dispoñibles para actuar en todo tipo de eventos: festas, concertos, celebracións e moito máis. Contacta connosco para máis información.' }}
            </p>
            <router-link to="/contact" custom v-slot="{ navigate }">
              <Button variant="primary" size="lg" @click="navigate">
                <i class="fas fa-envelope"></i>
                {{ tr('events.ctaButton') }}
              </Button>
            </router-link>
          </div>
        </Card>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted } from 'vue';
import { useHead } from '@vueuse/head';
import Card from '../ui/Card.vue';
import Button from '../ui/Button.vue';
import { useAnalytics } from '../../composables/useAnalytics'
import { buildHeadConfig, getBreadcrumbJsonLd, getEventsJsonLd } from '../../seo/meta';

// Access i18n
const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const tr = (key: string) => ($t ? $t(key) : key);

// Analytics
const { trackEvent } = useAnalytics()

const eventsLd = getEventsJsonLd([
  {
    name: 'Concerto da Banda de Música de Noia',
    description:
      'Actuación da Banda de Música de Noia con repertorio galego e clásicos populares. Entrada libre ata completar aforo.',
    startDate: '2025-12-20T20:30:00+01:00',
    endDate: '2025-12-20T22:00:00+01:00',
    locationName: 'Praza do Tapal, Noia',
    locationAddress: 'Praza do Tapal, 15200 Noia, A Coruña, Galicia, España',
    url: '/events',
  },
])

useHead(
  buildHeadConfig('events', {
    jsonLd: [...eventsLd, getBreadcrumbJsonLd('events')],
  }),
)

// Track page view
onMounted(() => {
  trackEvent('events_page_viewed', 'engagement', 'Events Page')
})
</script>

<style scoped>
/* Events Page */
.events-page {
  background: white;
}

/* Hero Section */
.events-hero {
  position: relative;
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-music);
  overflow: hidden;
  padding: var(--spacing-20) var(--spacing-4);
}

.events-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

.hero-content {
  position: relative;
  text-align: center;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: var(--font-weight-bold);
  color: white;
  margin: 0 0 var(--spacing-4) 0;
  font-family: var(--font-family-serif);
  text-align: center;
}

.hero-subtitle {
  font-size: var(--font-size-xl);
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 var(--spacing-6) 0;
  text-align: center;
}

.hero-decoration {
  display: flex;
  gap: var(--spacing-6);
  justify-content: center;
  margin-top: var(--spacing-8);
}

.music-note {
  font-size: 2.5rem;
  color: var(--color-secondary-400);
  animation: float 3s ease-in-out infinite;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);
}

.music-note:nth-child(2) {
  animation-delay: 1s;
}

.music-note:nth-child(3) {
  animation-delay: 2s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
  }
}

/* Calendar Section */
.calendar-section {
  padding: var(--spacing-20) 0 var(--spacing-24) 0;
  background: var(--color-neutral-50);
}

.calendar-card {
  max-width: 1200px;
  margin: 0 auto;
}

.calendar-header {
  text-align: center;
  margin-bottom: var(--spacing-8);
}

.calendar-title {
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin: 0 0 var(--spacing-3) 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-3);
  font-family: var(--font-family-serif);
}

.calendar-title i {
  color: var(--color-secondary-500);
}

.calendar-description {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  margin: 0 auto;
  max-width: 62ch;
  text-align: center;
  text-wrap: balance;
}

.calendar-wrapper {
  position: relative;
  background: var(--color-neutral-50);
  border-radius: var(--border-radius-lg);
  overflow: auto;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
  padding: var(--spacing-5);
  min-height: 600px;
}

.calendar-iframe {
  width: 100%;
  height: 700px;
  min-height: 600px;
  border: none;
  display: block;
  background: white;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
}

@media (max-width: 768px) {
  .calendar-wrapper {
    min-height: 500px;
    padding: var(--spacing-4);
    overflow-x: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .calendar-iframe {
    height: 800px;
    min-height: 500px;
    width: 100%;
  }
}

/* Info Section */
.info-section {
  padding: var(--spacing-20) 0 var(--spacing-24) 0;
  background: white;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-10);
  max-width: 1200px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .info-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.info-card {
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.info-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-2xl);
}

.info-icon {
  width: 80px;
  height: 80px;
  background: var(--gradient-music);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-6) auto;
  font-size: 2rem;
  color: white;
  box-shadow: var(--shadow-lg);
}

.info-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin: 0 0 var(--spacing-3) 0;
  font-family: var(--font-family-serif);
}

.info-text {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin: 0;
}

/* CTA Section */
.cta-section {
  padding: var(--spacing-20) 0;
  background: linear-gradient(180deg, rgba(79, 148, 160, 0.08) 0%, var(--color-neutral-50) 100%);
  position: relative;
  overflow: hidden;
}

.cta-card {
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  box-shadow: var(--shadow-2xl);
}

.cta-content {
  text-align: center;
  padding: var(--spacing-4);
}

.cta-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin-bottom: var(--spacing-6);
  font-family: var(--font-family-serif);
  line-height: 1.2;
}

.cta-text {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-8);
  line-height: 1.8;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  font-weight: var(--font-weight-normal);
}

@media (max-width: 768px) {
  .events-hero {
    min-height: 40vh;
  }

  .hero-subtitle {
    font-size: var(--font-size-lg);
  }

  .music-note {
    font-size: 2rem;
  }
}

/* Refresh 2026 - visual coherence with Home */
.events-page {
  background: transparent;
}

.events-hero {
  min-height: 44vh;
  padding: clamp(2.8rem, 6vw, 5.2rem) var(--spacing-4);
  background:
    radial-gradient(circle at 13% 18%, rgba(219, 174, 80, 0.2), rgba(219, 174, 80, 0) 40%),
    linear-gradient(132deg, #10293b 0%, #1a3c54 52%, #2f6285 100%);
}

.events-hero::before {
  background: radial-gradient(circle at 82% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 45%);
}

.hero-title {
  letter-spacing: var(--letter-spacing-tight);
}

.calendar-section,
.info-section {
  background: transparent;
}

.calendar-card,
.info-card,
.cta-card {
  background: var(--surface-card-strong);
  border: 1px solid rgba(16, 41, 59, 0.12);
  box-shadow: var(--shadow-xl);
}

.calendar-wrapper {
  background: var(--surface-muted);
  border: 1px solid rgba(16, 41, 59, 0.1);
}

.calendar-iframe {
  background: rgba(255, 255, 255, 0.94);
}

.info-icon {
  background: linear-gradient(132deg, #173c56 0%, #2f6285 55%, #a77123 100%);
}

.cta-section {
  background: none;
}

.cta-card {
  background:
    radial-gradient(circle at 15% 14%, rgba(219, 174, 80, 0.18), rgba(219, 174, 80, 0) 45%),
    linear-gradient(140deg, #0f293b 0%, #173c56 56%, #1f584d 100%);
  border-color: rgba(255, 255, 255, 0.14);
}

.cta-title,
.cta-text {
  color: #f8f5ef;
}
</style>
